import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { Icon } from './Icon'

export type AbwPillSelectOption = { value: string; label: string }

export type AbwPillSelectProps = {
	/** Pill label when nothing is selected. */
	label: string
	value?: string
	options: AbwPillSelectOption[]
	/** Called with the picked value, or undefined when the current pick is cleared. */
	onChange: (value: string | undefined) => void
	className?: string
}

/**
 * Pill-shaped dropdown filter (design's explore/instructor filter row):
 * outlined pill that turns brand-blue when a value is selected and opens a
 * small popover listbox. Picking the selected option again clears it.
 */
export const PillSelect: React.FC<AbwPillSelectProps> = ({ label, value, options, onChange, className }) => {
	const [open, setOpen] = useState(false)
	const rootRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		if (!open) return
		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') setOpen(false)
		}
		document.addEventListener('keydown', onKey)
		return () => document.removeEventListener('keydown', onKey)
	}, [open])

	const selected = options.find(o => o.value === value)

	return (
		<Root ref={rootRef} className={className}>
			<Pill type="button" data-active={!!selected} aria-haspopup="listbox" aria-expanded={open} onClick={() => setOpen(v => !v)}>
				<span className="pill-label">{selected?.label ?? label}</span>
				<Icon name="chevron-down" size={14} />
			</Pill>
			{open && (
				<>
					<Scrim onClick={() => setOpen(false)} />
					<Popover role="listbox">
						{options.map(o => {
							const isSelected = o.value === value
							return (
								<Option
									key={o.value}
									type="button"
									role="option"
									aria-selected={isSelected}
									data-selected={isSelected}
									onClick={() => {
										onChange(isSelected ? undefined : o.value)
										setOpen(false)
									}}
								>
									{o.label}
									{isSelected && (
										<span className="check">
											<Icon name="check" size={14} />
										</span>
									)}
								</Option>
							)
						})}
					</Popover>
				</>
			)}
		</Root>
	)
}

const Root = styled.div`
	position: relative;
	flex: none;
`
const Pill = styled.button`
	height: 32px;
	padding: 0 12px;
	border-radius: var(--r-full);
	border: 1px solid var(--border);
	background: var(--surface-card);
	color: var(--text-primary);
	font: var(--fw-bold) var(--fs-label) var(--font-sans);
	font-family: var(--font-sans);
	cursor: pointer;
	display: flex;
	align-items: center;
	gap: 6px;
	white-space: nowrap;
	max-width: 220px;
	transition: background var(--dur-fast) var(--ease), color var(--dur-fast) var(--ease), border-color var(--dur-fast) var(--ease);
	.pill-label {
		overflow: hidden;
		text-overflow: ellipsis;
	}
	&[data-active='true'] {
		background: var(--blue);
		border-color: var(--blue);
		color: #fff;
	}
`
const Scrim = styled.div`
	position: fixed;
	inset: 0;
	z-index: 39;
`
const Popover = styled.div`
	position: absolute;
	top: calc(100% + 6px);
	inset-inline-start: 0;
	z-index: 40;
	background: var(--surface-card);
	border: 1px solid var(--border);
	border-radius: var(--r-lg);
	box-shadow: var(--sh-overlay);
	min-width: 176px;
	max-height: 280px;
	overflow-y: auto;
	padding: 4px;
	display: flex;
	flex-direction: column;
	gap: 2px;
`
const Option = styled.button`
	border: none;
	background: transparent;
	color: var(--text-primary);
	border-radius: var(--r-sm);
	padding: 10px 12px;
	font: var(--fw-regular) 13px var(--font-sans);
	font-family: var(--font-sans);
	cursor: pointer;
	text-align: start;
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 8px;
	white-space: nowrap;
	&:hover {
		background: var(--blue-08);
	}
	&[data-selected='true'] {
		background: var(--blue-12);
		font-weight: var(--fw-bold);
	}
	.check {
		color: var(--blue);
		display: flex;
	}
`
